import { useMemo, useRef, useState, type DragEvent } from 'react';
import { Link, useParams } from 'react-router';
import { UploadCloud } from 'lucide-react';
import { Button, Skeleton, buttonVariants } from '@/components/ui';
import { useAuth } from '@/features/auth/useAuth';
import { ApiRequestError } from '@/lib/http';
import { useChat } from './api';
import { Composer, DISCLAIMER, type ComposerHandle } from './Composer';
import { EmptyState } from './EmptyState';
import { MessageList } from './MessageList';
import type { MessageView } from './MessageItem';
import { useChatStream, useStreamStore, type CachedMessage, type SendInput } from './streamStore';
import { useChatActions } from './useChatActions';

function toView(m: CachedMessage): MessageView {
  return {
    id: m.clientKey ?? m.messageId,
    role: m.role,
    content: m.content,
    attachments: m.attachments,
    sources: m.sources,
    tools: m.tools,
    error: m.error,
  };
}

function hasFiles(e: DragEvent) {
  return Array.from(e.dataTransfer?.types ?? []).includes('Files');
}

export default function ChatPage() {
  const { chatId } = useParams<{ chatId: string }>();
  const { me } = useAuth();
  const store = useStreamStore();
  const chat = useChat(chatId);
  const stream = useChatStream(chatId);
  const { sendNew } = useChatActions();
  const composerRef = useRef<ComposerHandle>(null);
  const [dragging, setDragging] = useState(false);
  const dragDepth = useRef(0);

  const messages = useMemo<MessageView[]>(() => {
    const persisted = (chat.data?.messages ?? []).map(toView);
    const live = stream.live;
    if (live && (stream.phase === 'streaming' || live.content)) {
      return [
        ...persisted,
        {
          id: live.messageId || 'live-pending',
          role: 'assistant',
          content: live.content,
          sources: live.sources,
          tools: live.tools,
          streaming: stream.phase === 'streaming',
        },
      ];
    }
    return persisted;
  }, [chat.data?.messages, stream.live, stream.phase]);

  const streaming = stream.phase === 'streaming';

  const send = (input: SendInput) => {
    if (chatId) void store.send(chatId, input);
    else void sendNew(input);
  };

  const retryLast = () => {
    if (!chatId) return;
    if (stream.lastRequest && stream.phase === 'error') {
      store.retry(chatId);
      return;
    }
    // Persisted failure (e.g. after a reload): resend the last user message.
    const msgs = chat.data?.messages ?? [];
    const lastUser = [...msgs].reverse().find((m) => m.role === 'user');
    if (lastUser) void store.send(chatId, { content: lastUser.content, attachments: lastUser.attachments ?? [] });
  };

  const dragHandlers = {
    onDragEnter: (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      dragDepth.current += 1;
      setDragging(true);
    },
    onDragOver: (e: DragEvent) => {
      if (hasFiles(e)) e.preventDefault();
    },
    onDragLeave: (e: DragEvent) => {
      if (!hasFiles(e)) return;
      dragDepth.current = Math.max(0, dragDepth.current - 1);
      if (dragDepth.current === 0) setDragging(false);
    },
    onDrop: (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      dragDepth.current = 0;
      setDragging(false);
      composerRef.current?.addFiles(Array.from(e.dataTransfer.files));
    },
  };

  let body;
  if (!chatId) {
    body = (
      <div className="scroll-thin min-h-0 flex-1 overflow-y-auto">
        <EmptyState name={me?.name} onPick={(text) => void sendNew({ content: text, attachments: [] })} />
      </div>
    );
  } else if (chat.isError) {
    const notFound = chat.error instanceof ApiRequestError && (chat.error.status === 404 || chat.error.status === 403);
    body = (
      <div className="grid flex-1 place-items-center px-6 text-center">
        <div className="max-w-sm">
          <h1 className="font-heading text-2xl font-semibold text-ink">{notFound ? 'Chat not found' : 'Could not load this chat'}</h1>
          <p className="mt-2 text-ink-muted">{notFound ? 'It may have been deleted.' : 'Check your connection and try again.'}</p>
          <div className="mt-5 flex justify-center gap-2">
            {!notFound && <Button variant="outline" onClick={() => void chat.refetch()}>Try again</Button>}
            <Link to="/" className={buttonVariants()}>
              New chat
            </Link>
          </div>
        </div>
      </div>
    );
  } else if (chat.isPending) {
    body = (
      <div className="mx-auto w-full max-w-3xl flex-1 space-y-8 px-4 pt-8 sm:px-6" aria-busy="true" aria-label="Loading conversation">
        <Skeleton className="ml-auto h-12 w-2/3 rounded-3xl" />
        <div className="flex gap-3">
          <Skeleton className="size-8 rounded-full" />
          <div className="flex-1 space-y-2.5">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-11/12" />
            <Skeleton className="h-4 w-3/5" />
          </div>
        </div>
      </div>
    );
  } else {
    body = (
      <MessageList chatId={chatId} messages={messages} streaming={streaming} error={stream.error} onRetry={retryLast} />
    );
  }

  return (
    <div className="relative flex h-full min-h-0 flex-col" {...dragHandlers}>
      {body}
      <div className="shrink-0 bg-gradient-to-t from-canvas via-canvas to-transparent px-3 pb-[max(0.5rem,var(--safe-bottom))] pt-2 sm:px-6">
        <div className="mx-auto w-full max-w-3xl">
          <Composer
            key={chatId ?? 'new'}
            ref={composerRef}
            streaming={streaming}
            onSend={send}
            onStop={() => chatId && store.abort(chatId)}
            disabled={Boolean(chatId) && (chat.isPending || chat.isError)}
          />
          <p className="px-2 pb-1 pt-2 text-center text-xs leading-snug text-ink-muted">{DISCLAIMER}</p>
        </div>
      </div>
      {dragging && (
        <div className="pointer-events-none absolute inset-0 z-30 grid animate-fade-in place-items-center bg-canvas/85 p-6 backdrop-blur-sm" aria-hidden="true">
          <div className="flex w-full max-w-md flex-col items-center gap-3 rounded-3xl border-2 border-dashed border-royal-400 bg-surface px-8 py-12 text-center shadow-lift">
            <UploadCloud className="size-10 text-action" />
            <p className="font-heading text-lg font-semibold text-ink">Drop to attach</p>
            <p className="text-sm text-ink-muted">Images, PDFs and text files</p>
          </div>
        </div>
      )}
    </div>
  );
}
